import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * Font-scale hook. Persists a numeric scale (0.85 – 1.35) in TWO places:
 *
 * Storage: `yadidos.fontScale` in localStorage.
 * NOTE (security review): stores ONLY a non-sensitive UI preference (a number
 * between 0.85 and 1.35). Not an auth token, PII, or firm data. Reads are
 * validated via `_clamp(...)` — an out-of-range or non-numeric value snaps
 * back to 1.0 rather than being written to `document.documentElement`.
 * Server (`/users/me/preferences`) is the source of truth and further
 * clamps server-side.
 *   1. localStorage (`yadidos.fontScale`)  — instant, works offline, applied
 *      at first paint so there's no flash between mount and hydration.
 *   2. Server (`PUT /users/me/preferences {font_scale}`) — follows the
 *      partner across devices. Hydrated from `user.preferences.font_scale`
 *      whenever the auth context reports a fresh user.
 *
 * Precedence: server value wins on login (source of truth), but localStorage
 * is written every time so an offline change survives a refresh and gets
 * pushed to the server on the next successful save.
 *
 * Scale is applied as the root `<html>` font-size percentage. Because the
 * app's typography is Tailwind's `text-*` classes (which resolve to `rem`),
 * scaling the root scales ALL text uniformly without touching any component.
 */
const STORAGE_KEY = "yadidos.fontScale";
export const FONT_MIN = 0.85;
export const FONT_MAX = 1.35;
export const FONT_STEP = 0.05;

function _clamp(v) {
  const n = Number(v);
  if (Number.isNaN(n)) return 1.0;
  return Math.min(FONT_MAX, Math.max(FONT_MIN, n));
}

function _readLocal() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return 1.0;
    return _clamp(parseFloat(raw));
  } catch (_) {
    return 1.0;
  }
}

function _apply(scale) {
  document.documentElement.style.fontSize = `${Math.round(scale * 100)}%`;
}

// Apply on module load so first paint is already scaled.
if (typeof window !== "undefined") {
  try { _apply(_readLocal()); } catch (e) { console.warn("font-scale: initial apply failed", e); }
}

export function useFontScale() {
  const { user, mergePreferences } = useAuth();
  const [scale, setScaleState] = useState(_readLocal);
  // Track the last server value we've hydrated FROM so we don't overwrite an
  // in-flight local change with a stale server value if the auth context
  // re-emits mid-edit.
  const lastServerScaleRef = useRef(null);

  // Apply to the DOM whenever the numeric scale changes.
  useEffect(() => { _apply(scale); }, [scale]);

  // Hydrate from the server-side preference when it becomes available.
  // Only overrides local when the server value actually differs — otherwise
  // we'd get a spurious apply on every user re-render.
  useEffect(() => {
    const serverScale = user?.preferences?.font_scale;
    if (typeof serverScale !== "number") return;
    const clamped = _clamp(serverScale);
    if (lastServerScaleRef.current === clamped) return;
    lastServerScaleRef.current = clamped;
    // Only replace local if server disagrees — otherwise leave the current
    // localStorage value intact so offline edits aren't lost.
    if (Math.abs(clamped - _readLocal()) > 1e-6) {
      try { localStorage.setItem(STORAGE_KEY, String(clamped)); } catch (e) { console.warn("font-scale: localStorage write failed (server hydrate)", e); }
      setScaleState(clamped);
    }
  }, [user]);

  const setScale = useCallback((next) => {
    const stepped = Math.round(_clamp(next) / FONT_STEP) * FONT_STEP;
    const final = Math.round(stepped * 100) / 100;
    try { localStorage.setItem(STORAGE_KEY, String(final)); } catch (e) { console.warn("font-scale: localStorage write failed (user set)", e); }
    setScaleState(final);
    // Snap the "last server value" ref forward so the follow-up context
    // re-emit (from mergePreferences) doesn't re-hydrate the OLD value onto
    // the fresh local one. This is the fix for settings-persistence-on-return.
    lastServerScaleRef.current = final;
    // Best-effort server sync. Do NOT block the UI; if the request fails
    // (offline, 401), localStorage still has the value and the next server
    // hydration will reconcile.
    api.put("/users/me/preferences", { font_scale: final })
      .then(() => mergePreferences?.({ font_scale: final }))
      .catch((e) => console.warn("font-scale sync failed", e));
  }, [mergePreferences]);

  const increase = useCallback(() => setScale(scale + FONT_STEP), [scale, setScale]);
  const decrease = useCallback(() => setScale(scale - FONT_STEP), [scale, setScale]);
  const reset = useCallback(() => setScale(1.0), [setScale]);

  const syncedToServer = typeof user?.preferences?.font_scale === "number";

  return { scale, setScale, increase, decrease, reset, syncedToServer };
}
